import type { Event } from "@package/ui-gql/index-analytics";
import { OrientationType } from "@package/ui-gql/index-data-access";
import { type SliceWallScreenState } from "@package/ui-wall-engine";
import { createContext, useContext } from "react";
import type { ContainerSize } from "../../utils/use-container-size";
import { TRIVIA_CONFIG_DEFAULTS } from "./trivia-constants";
import {
  createInitialTriviaMachineState,
  type TriviaMachineState,
} from "./trivia-reducer";
import type {
  TriviaAnswerModel,
  TriviaConfig,
  TriviaHelp,
  TriviaIssue,
  TriviaPlaythrough,
  TriviaQuestionModel,
  TriviaScore,
} from "./trivia-types";

export type TriviaContextState = {
  screen?: SliceWallScreenState;
  config: TriviaConfig;
  issues: TriviaIssue[];
  playthrough: TriviaPlaythrough;
  currentQuestion?: TriviaQuestionModel;
  machine: TriviaMachineState;
  score: TriviaScore;
  containerSize: ContainerSize;
  analytics: Event[];
  analyticsLoading: boolean;
  start: () => void;
  submitAnswer: (answer: TriviaAnswerModel) => void;
  submitTimeout: () => void;
  advance: () => void;
  activateHelp: (help: TriviaHelp) => void;
};

const defaultPlaythrough: TriviaPlaythrough = { seed: 0, questions: [] };

const defaultScore: TriviaScore = {
  points: 0,
  maxPoints: 0,
  correctCount: 0,
  total: 0,
  percentage: 0,
};

const defaultContainerSize: ContainerSize = {
  width: 0,
  height: 0,
  orientation: OrientationType.Portrait,
};

export const defaultTriviaContextState: TriviaContextState = {
  config: TRIVIA_CONFIG_DEFAULTS,
  issues: [],
  playthrough: defaultPlaythrough,
  machine: createInitialTriviaMachineState(TRIVIA_CONFIG_DEFAULTS, 0),
  score: defaultScore,
  containerSize: defaultContainerSize,
  analytics: [],
  analyticsLoading: false,
  start: () => {},
  submitAnswer: () => {},
  submitTimeout: () => {},
  advance: () => {},
  activateHelp: () => {},
};

export const TriviaContext = createContext<TriviaContextState>(
  defaultTriviaContextState,
);
export const useTriviaContext = () => useContext(TriviaContext);
